<template>
  <el-dialog :visible.sync="dialogVisible" width="88%" top="3vh" append-to-body custom-class="innovation-score-dialog"
    :close-on-click-modal="false" @closed="$emit('closed')">
    <template slot="title">
      <span class="dialog-title">企业创新积分数据展示</span>
    </template>
    <div class="score-detail" v-loading="loading">
      <div v-if="!loading && state !== 'READY'" class="state-panel">
        <i :class="state === 'CALCULATING' ? 'el-icon-loading' : 'el-icon-warning-outline'"></i>
        <h2>{{ stateTitle }}</h2>
        <p>{{ stateMessage }}</p>
        <el-button v-if="state !== 'CALCULATING'" type="primary" @click="load(false)">重新查询</el-button>
      </div>
      <template v-else-if="state === 'READY'">
        <header class="profile-hero">
          <h1>{{ displayValue(result.enterpriseName) }}</h1>
          <div class="hero-actions">
            <el-button icon="el-icon-refresh" :loading="regenerating" @click="regenerate">重新生成</el-button>
            <div class="hero-score"><span>企业创新积分</span><strong>{{ formatScore(result.totalScore) }}</strong><small>最高130分</small></div>
          </div>
        </header>

        <section class="content-section">
          <div class="section-heading"><h2>企业基本信息</h2></div>
          <dl class="company-info-grid">
            <div v-for="item in companyProfile" :key="item.label" :class="{ wide: item.wide }" class="company-info-item">
              <dt>{{ item.label }}</dt><dd>{{ item.value }}</dd>
            </div>
          </dl>
        </section>

        <section class="content-section score-overview-section">
          <div class="section-heading"><h2>企业创新积分</h2></div>
          <div class="score-overview">
            <div class="total-score"><strong>{{ formatScore(result.totalScore) }}</strong><span>企业创新积分</span></div>
            <div class="score-groups">
              <section v-for="group in scoreOverviewGroups" :key="group.label">
                <div class="score-group-heading"><h3>{{ group.label }}</h3><strong>{{ formatScore(group.score) }}分</strong></div>
                <dl><div v-for="item in group.items" :key="item.label"><dt>{{ item.label }}</dt><dd>{{ formatScore(item.score) }}分</dd></div></dl>
              </section>
            </div>
            <div class="score-year"><span>核算年度</span><strong>{{ result.accountingYear }}年度</strong></div>
          </div>
        </section>

        <section class="content-section">
          <div class="section-heading"><h2>创新能力分析与建议</h2></div>
          <div v-if="result.aiAdvice && result.aiAdvice.status === 'READY'" class="advice-content">
            <p class="advice-summary">{{ result.aiAdvice.summary || '暂未生成综合评价' }}</p>
            <div class="advice-columns">
              <div><h3><i class="el-icon-circle-check"></i> 优势表现</h3><ul><li v-for="item in result.aiAdvice.strengths" :key="item">{{ item }}</li></ul></div>
              <div><h3><i class="el-icon-warning-outline"></i> 主要不足</h3><ul><li v-for="item in result.aiAdvice.weaknesses" :key="item">{{ item }}</li></ul></div>
              <div><h3><i class="el-icon-s-promotion"></i> 改进方向</h3><ul><li v-for="item in result.aiAdvice.actions" :key="item">{{ item }}</li></ul></div>
            </div>
          </div>
          <div v-else class="advice-state">
            <i :class="result.aiAdvice && result.aiAdvice.status === 'GENERATING' ? 'el-icon-loading' : 'el-icon-info'"></i>
            <span>{{ adviceMessage }}</span>
          </div>
        </section>

        <section class="content-section">
          <div class="section-heading"><h2>企业创新积分得分情况</h2></div>
          <div class="core-layout">
            <div ref="coreChart" class="core-chart" role="img" aria-label="核心指标得分玫瑰图"></div>
            <div class="indicator-list" aria-label="核心指标分类与指标得分">
              <section v-for="(group, groupIndex) in coreIndicatorGroups" :key="group.category">
                <h3><span :style="{ backgroundColor: group.color }" class="category-swatch"></span>{{ group.category }}<strong>{{ formatScore(group.score) }}分</strong></h3>
                <ul>
                  <li v-for="(item, itemIndex) in group.items" :key="item.name">
                    <span :style="{ backgroundColor: item.color }" class="indicator-swatch"></span>
                    <span class="indicator-name">
                      <span>{{ item.name }}</span>
                      <span v-if="item.explanation" class="indicator-explanation">
                        <button
                          type="button"
                          class="indicator-explanation-trigger"
                          :aria-label="`${item.name}指标解释`"
                          :aria-describedby="`manage-indicator-explanation-${groupIndex}-${itemIndex}`"
                        ><span aria-hidden="true" class="indicator-exclamation-mark">!</span></button>
                        <span
                          :id="`manage-indicator-explanation-${groupIndex}-${itemIndex}`"
                          class="indicator-explanation-content"
                          role="tooltip"
                        >{{ item.explanation }}</span>
                      </span>
                    </span>
                    <strong>{{ formatScore(item.score) }}分</strong>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </section>

        <section class="content-section">
          <div class="section-heading"><h2>加分项</h2></div>
          <div class="core-layout bonus-layout">
            <div ref="bonusChart" class="bonus-chart" role="img" aria-label="加分项得分组成环形图"></div>
            <div class="indicator-list bonus-indicator-list" aria-label="加分项指标与得分">
              <section>
                <h3><span :style="{ backgroundColor: bonusIndicatorGroup.color }" class="category-swatch"></span>{{ bonusIndicatorGroup.category }}<strong>{{ formatScore(bonusIndicatorGroup.score) }}分</strong></h3>
                <ul>
                  <li v-for="(item, itemIndex) in bonusIndicatorGroup.items" :key="item.name">
                    <span :style="{ backgroundColor: item.color }" class="indicator-swatch"></span>
                    <span class="indicator-name">
                      <span>{{ item.name }}</span>
                      <span v-if="item.explanation" class="indicator-explanation">
                        <button
                          type="button"
                          class="indicator-explanation-trigger"
                          :aria-label="`${item.name}指标解释`"
                          :aria-describedby="`manage-bonus-indicator-explanation-${itemIndex}`"
                        ><span aria-hidden="true" class="indicator-exclamation-mark">!</span></button>
                        <span
                          :id="`manage-bonus-indicator-explanation-${itemIndex}`"
                          class="indicator-explanation-content"
                          role="tooltip"
                        >{{ item.explanation }}</span>
                      </span>
                    </span>
                    <strong>{{ formatScore(item.score) }}分</strong>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </section>

        <section class="content-section">
          <div class="section-heading"><h2>积分构成分析</h2></div>
          <div class="composition-list">
            <article v-for="item in compositionItems" :key="item.name" :class="item.tone">
              <i :class="item.icon"></i><div><h3>{{ item.name }}</h3><p>{{ item.description }}</p></div>
              <div class="composition-score"><strong>{{ formatScore(item.score) }}</strong><small>构成占比{{ item.share }}%</small></div>
            </article>
          </div>
        </section>

        <section class="content-section history-section">
          <div class="section-heading"><h2>历年企业创新积分</h2></div>
          <div v-if="result.history && result.history.length" ref="historyChart" class="history-chart" role="img" aria-label="历年企业创新积分柱状图"></div>
          <el-empty v-else description="暂无历年积分数据" :image-size="72" />
        </section>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import echarts from 'echarts';

const API = 'apij2/admin-api/trade/enterprise-innovation-score';
const COLORS = ['#155eef', '#3784ff', '#69a5ff', '#008f7a', '#16b89a', '#55d6be', '#f59e0b', '#f97316', '#ef4444'];
const BONUS_INDICATOR_COLORS = ['#f97316', '#22c55e', '#a855f7'];
const INDICATOR_EXPLANATIONS = Object.freeze({
  研发费用金额: '研发费用主要包括研发活动的人工费用、直接投入费用、用于研发活动的仪器、设备的折旧费、用于研发活动的软件、专利权、非专利技术的摊销费用、新产品设计费、新工艺规程制定费以及其他研发活动相关费用。',
  研发费用占营业收入比例: '填报期企业研发费用总额÷同期营业收入总额。',
  研发人员占比: '填报期企业参加科研项目活动的人员总数÷企业从业人员期末人数。',
  发明专利授权量: '企业拥有的经知识产权行政部门授权且在有效期内的发明专利件数。',
  每百人研发人员知识产权数量: '知识产权拥有件数÷研发人员（百人）。知识产权包括国内外专利、植物新品种、集成电路布图设计专有权、软件著作权等。',
  企业技术合同成交额: '填报期内企业吸纳和输出的技术合同成交总额（开发合作、转让合同）。',
  主营业务收入: '填报期内企业销售技术产品、提供服务等主营业务取得的收入总额。',
  营业收入增长率: '填报期企业当年营业收入增加额÷上年营业收入总额。',
  净资产利润率: '填报期内企业净利润÷[（期初所有者权益+期末所有者权益）÷2]。'
});

const BONUS_INDICATOR_EXPLANATIONS = Object.freeze({
  科技项目与标准制定: '企业作为牵头或参与单位承担的地市级、省级及以上科技计划项目/课题累计总数量；作为第一单位获得的地市级、省级及以上科技奖励累计总数量；作为起草单位参与制定并已发布的国家标准、行业标准、地方标准的累计总数量。',
  融资额度: '企业获得天使投资、创投机构投资、银行贷款额等多渠道融资金额。',
  行业排名与科技资质: '企业或主导产品在国内外重点领域或细分领域行业排名情况，包括全球行业头部机构排名、国际标准组织认证排名、省部级及以上相关机构相关官方统计报告、国内权威市场研究机构报告。企业取得“科技领军企业”“国家高新技术企业”“科技型中小企业”“小巨人”“专精特新”等科技相关资质。'
});

function normalizeIndicatorName(name) {
  return String(name || '')
    .replace(/\s+/g, '')
    .replace(/[（(][^）)]*[）)]/g, '')
    .replace(/的/g, '');
}

const META = {
  创新投入: { description: '研发费用、研发人员', icon: 'el-icon-s-data', tone: 'tone-blue' },
  创新产出: { description: '发明专利、知识产权、技术合同', icon: 'el-icon-medal', tone: 'tone-green' },
  创新发展: { description: '主营收入、营收增长、盈利能力', icon: 'el-icon-s-marketing', tone: 'tone-purple' }
};

export default {
  name: 'EnterpriseInnovationScoreDetail',
  props: { value: Boolean, companyDataId: { type: Number, default: null } },
  data() {
    return { loading: false, regenerating: false, state: '', stateMessage: '', result: {}, pollTimer: null,
      pollStartedAt: 0, pollMessage: '', pollingRequest: false, coreChart: null, bonusChart: null, historyChart: null };
  },
  computed: {
    dialogVisible: { get() { return this.value; }, set(value) { this.$emit('input', value); } },
    stateTitle() {
      const titles = { CALCULATING: '创新积分正在生成中', INCOMPLETE_DATA: '企业数据待补录', RULE_ERROR: '评分规则暂不可用', NO_DATA: '暂未查询到企业信息', ERROR: '查询失败' };
      return titles[this.state] || '暂时无法读取积分结果';
    },
    adviceMessage() {
      if (this.pollMessage) return this.pollMessage;
      return (this.result.aiAdvice && this.result.aiAdvice.message) || '创新能力分析与建议正在生成中';
    },
    companyProfile() {
      const profile = this.result.companyProfile || {};
      return [
        { label: '企业名称', value: this.displayValue(profile.companyName || this.result.enterpriseName) },
        { label: '法定代表人', value: this.displayValue(profile.legalRepresentative) },
        { label: '统一社会信用代码', value: this.displayValue(profile.creditCode) },
        { label: '成立日期', value: this.displayValue(profile.establishmentDate) },
        { label: '单位住所', value: this.displayValue(profile.address), wide: true },
        { label: '企业阶段', value: this.displayValue(this.result.enterpriseStageName) },
        { label: '核算年度', value: this.result.accountingYear ? `${this.result.accountingYear}年度` : '-' }
      ];
    },
    coreIndicatorGroups() {
      const core = (this.result.scoreBreakdown || {}).core || {};
      const scores = (core.dimensions || []).reduce((map, item) => { map[item.name] = Number(item.score || 0); return map; }, {});
      const groups = []; const groupMap = {};
      (core.indicators || []).forEach((item, index) => {
        const category = item.category || '其他';
        if (!groupMap[category]) {
          groupMap[category] = { category, score: scores[category] || 0, color: COLORS[index % COLORS.length], items: [] };
          groups.push(groupMap[category]);
        }
        groupMap[category].items.push({
          name: item.name,
          score: Number(item.score || 0),
          color: COLORS[index % COLORS.length],
          explanation: INDICATOR_EXPLANATIONS[normalizeIndicatorName(item.name)] || ''
        });
      });
      return groups;
    },
    bonusIndicatorGroup() {
      const bonus = (this.result.scoreBreakdown || {}).bonus || {};
      return {
        category: '创新影响',
        score: Number(bonus.score || 0),
        color: '#e85d04',
        items: (bonus.dimensions || []).map((item, index) => ({
          name: item.name,
          score: Number(item.score || 0),
          color: BONUS_INDICATOR_COLORS[index % BONUS_INDICATOR_COLORS.length],
          explanation: BONUS_INDICATOR_EXPLANATIONS[normalizeIndicatorName(item.name)] || ''
        }))
      };
    },
    scoreOverviewGroups() {
      const breakdown = this.result.scoreBreakdown || {};
      return [
        { label: '核心指标得分', score: Number((breakdown.core || {}).score || 0), items: ((breakdown.core || {}).dimensions || []).map(item => ({ label: item.name, score: item.score })) },
        { label: '加分项得分', score: Number((breakdown.bonus || {}).score || 0), items: ((breakdown.bonus || {}).dimensions || []).map(item => ({ label: item.name, score: item.score })) }
      ];
    },
    compositionItems() {
      const breakdown = this.result.scoreBreakdown || {}; const bonus = breakdown.bonus || {};
      const base = Number((breakdown.core || {}).score || 0) + Number(bonus.score || 0);
      const share = value => base > 0 ? (Number(value || 0) / base * 100).toFixed(1) : '0.0';
      const items = this.coreIndicatorGroups.map(group => ({ name: group.category, score: group.score, share: share(group.score), ...(META[group.category] || {}) }));
      items.push({ name: '创新影响加分', description: '科技项目、融资额度、行业排名与科技资质', icon: 'el-icon-star-on', tone: 'tone-orange', score: Number(bonus.score || 0), share: share(bonus.score) });
      return items;
    }
  },
  watch: {
    value(value) { if (value && this.companyDataId) this.load(); else if (!value) this.cleanup(); },
    companyDataId(value) { if (this.value && value) this.load(); }
  },
  mounted() { window.addEventListener('resize', this.resizeCharts); if (this.value && this.companyDataId) this.load(); },
  beforeDestroy() { window.removeEventListener('resize', this.resizeCharts); this.cleanup(); },
  methods: {
    async load(showLoading = true) {
      if (!this.companyDataId) return;
      if (!showLoading && this.pollingRequest) return;
      const wasReady = this.state === 'READY';
      if (showLoading) this.loading = true;
      else this.pollingRequest = true;
      try {
        const response = await this.http.get(`${this.urlConfig.devUrl}${API}/get?companyDataId=${this.companyDataId}`, true);
        this.applyResult(response.data || {});
      } catch (error) { this.state = 'ERROR'; this.stateMessage = '积分结果查询失败，请稍后重试'; }
      finally {
        this.loading = false; this.pollingRequest = false;
        if (this.state === 'READY' && (showLoading || !wasReady)) this.$nextTick(this.renderCharts);
      }
    },
    applyResult(data) {
      this.result = data; this.state = data.status || 'ERROR'; this.stateMessage = data.message || '';
      if (this.shouldPoll()) this.startPolling(); else this.stopPolling();
    },
    shouldPoll() { return this.state === 'CALCULATING' || (this.state === 'READY' && this.result.aiAdvice && this.result.aiAdvice.status === 'GENERATING'); },
    startPolling() {
      if (this.pollTimer) return;
      this.pollStartedAt = Date.now(); this.pollMessage = '';
      this.pollTimer = window.setInterval(() => {
        if (Date.now() - this.pollStartedAt >= 5 * 60 * 1000) { this.pollMessage = '生成时间较长，请稍后重新打开查看'; this.stopPolling(false); return; }
        this.load(false);
      }, 3000);
    },
    stopPolling(clearMessage = true) { if (this.pollTimer) window.clearInterval(this.pollTimer); this.pollTimer = null; if (clearMessage) this.pollMessage = ''; },
    async regenerate() {
      try {
        await this.$confirm(`确定重新生成“${this.result.enterpriseName}”${this.result.accountingYear}年度的创新积分和分析建议吗？`, '重新生成确认', { type: 'warning' });
        this.regenerating = true;
        await this.http.post(`${this.urlConfig.devUrl}${API}/regenerate?companyDataId=${this.companyDataId}`, {}, true);
        this.$message.success('已提交重新生成任务');
        await this.load(false);
      } catch (error) { /* cancellation and global request errors need no duplicate message */ }
      finally { this.regenerating = false; }
    },
    displayValue(value) { return value === null || value === undefined || String(value).trim() === '' ? '-' : String(value).trim(); },
    formatScore(value) { return Number(value || 0).toFixed(2); },
    renderCharts() {
      this.disposeCharts(); const breakdown = this.result.scoreBreakdown || {};
      this.coreChart = this.renderCore(this.$refs.coreChart, breakdown.core);
      this.bonusChart = this.renderBonus(this.$refs.bonusChart, breakdown.bonus);
      this.historyChart = this.renderHistory(this.$refs.historyChart, this.result.history || []);
    },
    renderCore(container, group) {
      if (!container || !group) return null; const chart = echarts.init(container);
      const source = (group.indicators || []).map((item, index) => ({ name: item.name, category: item.category, value: Number(item.score || 0), itemStyle: { color: COLORS[index % COLORS.length] } }));
      const hasScore = source.some(item => item.value > 0); const data = source.map(item => ({ ...item, value: hasScore ? item.value : 1, itemStyle: hasScore ? item.itemStyle : { color: '#e9eef3' } }));
      chart.setOption({ graphic: [
        { type: 'text', left: 'center', top: 'middle', silent: true,
          style: { text: this.formatScore(group.score), fill: '#1677ff', font: '600 28px sans-serif', textAlign: 'center', textVerticalAlign: 'middle' } },
        { type: 'text', left: 'center', top: '56%', silent: true,
          style: { text: `满分 ${this.formatScore(group.maxScore)}分`, fill: '#7b8794', font: '12px sans-serif', textAlign: 'center' } }
      ],
        tooltip: { trigger: 'item', formatter: p => `${p.data.category}<br/>${p.name}：${this.formatScore(p.data.value)}分` },
        series: [{ type: 'pie', roseType: 'radius', radius: ['32%', '78%'], center: ['50%', '50%'], minAngle: 4, label: { show: false }, itemStyle: { borderColor: '#fff', borderWidth: 3 }, data }] });
      return chart;
    },
    renderBonus(container, group) {
      if (!container || !group) return null; const chart = echarts.init(container); const colors = BONUS_INDICATOR_COLORS;
      const dimensions = (group.dimensions || []).map((item, index) => ({ name: item.name, value: Number(item.score || 0), itemStyle: { color: colors[index] } }));
      const score = Number(group.score || 0); const maximum = Number(group.maxScore || 0);
      const data = dimensions.concat([{ name: '未得分', value: Math.max(0, maximum - score), itemStyle: { color: '#e9eef3' }, label: { show: false } }]);
      chart.setOption({ graphic: [
        { type: 'text', left: 'center', top: 'middle', silent: true,
          style: { text: this.formatScore(score), fill: '#f97316', font: '600 27px sans-serif', textAlign: 'center', textVerticalAlign: 'middle' } },
        { type: 'text', left: 'center', top: '58%', silent: true,
          style: { text: `满分 ${this.formatScore(maximum)}`, fill: '#7b8794', font: '12px sans-serif', textAlign: 'center' } }
      ],
        tooltip: { trigger: 'item', formatter: p => p.name === '未得分' ? '' : `${p.name}<br/>${this.formatScore(p.value)}分` },
        series: [{ type: 'pie', radius: ['40%', '64%'], center: ['50%', '50%'], label: { show: false }, itemStyle: { borderColor: '#fff', borderWidth: 3 }, data }] });
      return chart;
    },
    renderHistory(container, history) {
      if (!container || !history.length) return null; const chart = echarts.init(container);
      chart.setOption({ tooltip: { trigger: 'axis' }, grid: { left: 52, right: 28, top: 38, bottom: 64 }, xAxis: { type: 'category', data: history.map(item => `${item.accountingYear}年`), axisLabel: { interval: 0, margin: 14 } }, yAxis: { type: 'value', min: 0, max: 130, interval: 20, splitLine: { lineStyle: { color: '#e7edf2' } } },
        series: [{ type: 'bar', data: history.map(item => Number(item.totalScore || 0)), barMaxWidth: 62, itemStyle: { color: '#1677ff', barBorderRadius: [5, 5, 0, 0] }, label: { show: true, position: 'top', color: '#1677ff', formatter: p => this.formatScore(p.value) } }] });
      return chart;
    },
    resizeCharts() { [this.coreChart, this.bonusChart, this.historyChart].forEach(chart => { if (chart) chart.resize(); }); },
    disposeCharts() { [this.coreChart, this.bonusChart, this.historyChart].forEach(chart => { if (chart) chart.dispose(); }); this.coreChart = this.bonusChart = this.historyChart = null; },
    cleanup() { this.stopPolling(); this.disposeCharts(); this.result = {}; this.state = ''; }
  }
};
</script>

<style lang="scss" scoped>
.score-detail { min-height: 520px; padding: 20px 28px 48px; background: #f2f5f8; color: #263442; box-sizing: border-box; text-align: left; }
.dialog-title { color: #17324d; font-weight: 600; }
.state-panel { min-height: 620px; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #fff; text-align: center; }
.state-panel > i { margin-bottom: 18px; color: #0878bd; font-size: 50px; }.state-panel h2 { margin: 0 0 10px; font-size: 22px; }.state-panel p { margin: 0 0 20px; color: #687685; }
.profile-hero { min-height: 120px; display: flex; align-items: center; justify-content: space-between; padding: 22px 28px; border-top: 4px solid #0878bd; background: #fff; box-sizing: border-box; }
.profile-hero h1 { margin: 0; color: #17324d; font-size: 25px; overflow-wrap: anywhere; }.hero-actions { display: flex; align-items: center; gap: 24px; margin-left: 24px; }
.hero-score { min-width: 220px; display: grid; grid-template-columns: auto auto; align-items: baseline; gap: 4px 12px; padding-left: 24px; border-left: 1px solid #dce4eb; }.hero-score strong { grid-row: span 2; color: #f97316; font-size: 50px; line-height: 1; }.hero-score span { color: #536170; font-weight: 600; }.hero-score small { color: #7a8591; }
.content-section { margin-top: 16px; padding: 22px; background: #fff; }.section-heading { margin-bottom: 18px; padding-bottom: 12px; border-bottom: 1px solid #e1e7ec; }.section-heading h2 { margin: 0; color: #17324d; font-size: 18px; }
.company-info-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); margin: 0; border-top: 1px solid #dfe6ec; border-left: 1px solid #dfe6ec; }.company-info-item { display: grid; grid-template-columns: 132px 1fr; border-right: 1px solid #dfe6ec; border-bottom: 1px solid #dfe6ec; }.company-info-item.wide { grid-column: 1/-1; }.company-info-item dt,.company-info-item dd { min-height: 48px; margin: 0; padding: 13px 14px; box-sizing: border-box; }.company-info-item dt { background: #f5f8fa; color: #5e6d7b; font-weight: 600; }.company-info-item dd { overflow-wrap: anywhere; }
.score-overview-section { border-left: 4px solid #d98a33; }.score-overview { padding: 16px 18px; background: #f8fafc; }.total-score { display: flex; flex-direction: column; align-items: center; padding-bottom: 14px; }.total-score strong { color: #f97316; font-size: 34px; }.total-score span { color: #6c7986; }.score-groups { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 24px; }.score-groups > section { padding: 14px 16px; background: #fff; }.score-group-heading,.score-groups dl > div,.score-year { display: flex; justify-content: space-between; gap: 16px; }.score-group-heading { padding-bottom: 10px; border-bottom: 1px solid #e8edf2; }.score-group-heading h3 { margin: 0; font-size: 15px; }.score-group-heading strong { color: #0878bd; font-size: 18px; }.score-groups dl { display: grid; gap: 7px; margin: 11px 0 0; }.score-groups dt,.score-groups dd { margin: 0; color: #687685; }.score-groups dd { color: #405162; font-weight: 600; }.score-year { margin-top: 14px; padding: 11px 16px 0; border-top: 1px solid #dde5ec; }
.advice-summary { margin: 0; padding: 14px 16px; background: #f5f8fa; line-height: 1.8; }.advice-columns { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 20px; margin-top: 18px; }.advice-columns > div { padding: 16px; background: #f8fafb; }.advice-columns h3 { margin: 0 0 10px; font-size: 15px; }.advice-columns ul { margin: 0; padding-left: 20px; color: #536170; line-height: 1.8; }.advice-state { min-height: 72px; display: flex; align-items: center; gap: 10px; padding: 0 18px; background: #f5f8fa; color: #637181; }
.core-layout { display: grid; grid-template-columns: minmax(300px,.9fr) minmax(420px,1.1fr); gap: 32px; align-items: center; }.core-chart,.bonus-chart { width: 100%; max-width: 460px; height: 360px; justify-self: center; }.bonus-chart { max-width: 420px; }.indicator-list section + section { margin-top: 9px; padding-top: 9px; border-top: 1px solid #edf1f5; }.indicator-list h3,.indicator-list li { display: grid; grid-template-columns: 9px minmax(0,1fr) auto; align-items: center; gap: 8px; margin: 0; line-height: 1.7; }.indicator-list ul { margin: 5px 0 0; padding: 0; list-style: none; }.indicator-list li { grid-template-columns: 7px minmax(0,1fr) auto; color: #667789; font-size: 12px; }.category-swatch,.indicator-swatch { display: block; width: 100%; height: 9px; border-radius: 2px; }.indicator-swatch { height: 7px; }.indicator-list strong { color: #405162; white-space: nowrap; }
.indicator-name { display: flex; min-width: 0; align-items: center; gap: 3px; }.indicator-name > span:first-child { min-width: 0; overflow-wrap: anywhere; }
.indicator-explanation { position: relative; display: inline-flex; flex: none; align-items: center; }
.indicator-explanation-trigger { position: relative; display: inline-flex; width: 10px; height: 10px; flex: none; align-items: center; justify-content: center; padding: 0; border: 1px solid #8db3cb; border-radius: 50%; background: #f7fbfe; box-sizing: border-box; color: #4f7893; font-size: 0; line-height: 1; cursor: help; }
.indicator-exclamation-mark { display: block; font-family: Verdana, Arial, sans-serif; font-size: 8px; font-weight: 700; line-height: 8px; }
.indicator-explanation-trigger:hover { border-color: #4f91bd; background: #eef7fc; color: #24678f; }.indicator-explanation-trigger:focus-visible { border-color: #4f91bd; outline: 2px solid rgba(79,145,189,.24); outline-offset: 2px; }
.indicator-explanation-content { position: absolute; bottom: calc(100% + 8px); left: 50%; z-index: 20; width: max-content; min-width: 220px; max-width: 360px; padding: 9px 11px; border: 1px solid transparent; border-radius: 4px; background: #eaf4fb; box-shadow: 0 4px 14px rgba(49,93,124,.16); box-sizing: border-box; color: #3f7395; font-size: 10px; line-height: 1.65; white-space: normal; overflow-wrap: anywhere; opacity: 0; visibility: hidden; pointer-events: none; transform: translate(-50%,4px); transition: opacity .15s ease,transform .15s ease,visibility .15s ease; }
.indicator-explanation-content::after { position: absolute; top: 100%; left: 50%; width: 8px; height: 8px; border-right: 1px solid transparent; border-bottom: 1px solid transparent; background: #eaf4fb; content: ''; transform: translate(-50%,-50%) rotate(45deg); }
.indicator-explanation:hover .indicator-explanation-content,.indicator-explanation:focus-within .indicator-explanation-content { opacity: 1; visibility: visible; transform: translate(-50%,0); }
.composition-list { display: grid; gap: 12px; }.composition-list article { --tone:#2563eb; --bg:#eff6ff; display: grid; grid-template-columns: 42px 1fr auto; gap: 16px; align-items: center; padding: 14px 16px; border: 1px solid #bfdbfe; border-radius: 6px; background: var(--bg); }.composition-list article > i { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; border-radius: 6px; background: var(--tone); color: #fff; font-size: 20px; }.composition-list h3 { margin: 0 0 4px; font-size: 15px; }.composition-list p { margin: 0; color: #6c7986; }.composition-score { display: flex; flex-direction: column; align-items: flex-end; color: var(--tone); }.composition-score strong { font-size: 22px; }.composition-score small { color: #788592; }.tone-green { --tone:#16a34a!important; --bg:#f0fdf4!important; border-color:#bbf7d0!important; }.tone-purple { --tone:#9333ea!important; --bg:#faf5ff!important; border-color:#e9d5ff!important; }.tone-orange { --tone:#ea580c!important; --bg:#fff7ed!important; border-color:#fed7aa!important; }
.history-section { padding-bottom: 28px; }.history-chart { width: 100%; height: 330px; }
@media (max-width: 1000px) { .score-detail { min-height: 420px; padding: 16px 16px 40px; }.advice-columns,.core-layout { grid-template-columns: 1fr; } }
</style>

<style lang="scss">
.innovation-score-dialog { max-width: 1440px; margin-bottom: 3vh; }
.innovation-score-dialog .el-dialog__header { padding: 18px 24px; border-bottom: 1px solid #e5e9ef; }
.innovation-score-dialog .el-dialog__body { max-height: calc(94vh - 61px); padding: 0; overflow-y: auto; }
@media (max-width: 1000px) { .innovation-score-dialog { width: 96% !important; } }
</style>
